import * as React from 'react';
import { t, type MessageKey } from '@reos/i18n';
import { systemClock } from '@reos/config';
import { FieldRow, FormSection, SaveStatus, type SaveStatusKind } from '@redhand/ui';
import { DomainApiError } from '../lib/api-error.js';
import { focusField, kodChyby } from '../lib/form-errors.js';
import { addPartyTaxIdentifier, createParty, listParties, type PartyDto } from '../lib/parties-api.js';
import { getBillingOverview } from '../lib/billing-api.js';
import { listCases } from '../lib/cases-api.js';
import { CASE_SEARCH_MIN_LENGTH, searchCasesByName } from '../lib/case-address-lookup.js';
import { useLeaveWarning } from '../lib/leave-guard.js';
import { rateLimitMessage, retryOnRateLimit } from '../lib/rate-limit-retry.js';
import { caseRoleCaption } from './case-aside.js';
import { commissionBlockedReason, matchingCases, soleInvoiceParty } from '../lib/invoice-case-pick.js';
import { FormLayout, FieldGrid } from './form-layout.js';
import { IcoAresField } from './ico-ares-field.js';
import {
  daysLate,
  filterInvoices,
  invoiceFilterCounts,
  invoiceStats,
  type InvoiceFilter,
} from '../lib/invoice-stats.js';
import {
  createCatalogItem,
  createCommissionInvoice,
  createFreeInvoice,
  deleteInvoiceDraft,
  downloadInvoicePdf,
  getCaseInvoiceContext,
  getInvoice,
  issueCreditNote,
  issueInvoice,
  listCatalogItems,
  listInvoices,
  markInvoicePaid,
  prefillCustomerFromParty,
  sendInvoiceEmail,
  syncInvoicePayment,
  updateCatalogItem,
  type CatalogItemDto,
  type CaseInvoiceContextDto,
  type InvoiceCustomerDto,
  type InvoiceDto,
  type InvoiceStatus,
  type InvoiceWithItemsDto,
  type CustomerPrefillSourceDto,
  type PartyPrefillDto,
  type PaymentsSnapshotDto,
} from '../lib/invoices-api.js';
import { listContracts } from '../lib/contracts-api.js';
import {
  commissionOffersFromContracts,
  commissionWarnings,
  type CommissionOffer,
  type CommissionOfferSource,
  type CommissionWarning,
} from '../lib/invoice-commission-basis.js';
import { DateField } from './date-field.js';
import { CzechNumberInput } from './czech-number-input.js';
import { formatPercent } from '../lib/properties.js';

/** Krok FAK5 bod 1 - stav uhrad z `syncInvoicePayment`, prelozeny do slovniku (ne cizi retezec z API). */
const PAYMENT_STATUS_LABEL_KEY: Readonly<Record<NonNullable<PaymentsSnapshotDto['paymentStatus']>, MessageKey>> = {
  unpaid: 'webAdmin.fakturaceDoklady.paymentStatus.unpaid',
  partially_paid: 'webAdmin.fakturaceDoklady.paymentStatus.partially_paid',
  paid: 'webAdmin.fakturaceDoklady.paymentStatus.paid',
  overpaid: 'webAdmin.fakturaceDoklady.paymentStatus.overpaid',
};

/**
 * Doklady pro klienty (krok FAK2) - prehled/detail/akce (bod 3), nova faktura
 * volna i ze zakazky (bod 1) a katalog sluzeb (bod 1). Sdilene komponenty pro
 * `/fakturace` (zalozky Doklady/Nova faktura/Nastaveni) a zalozku zakazky
 * "Obchod › Provize" (bod 4, `zakazka-detail.tsx`).
 */

const LOCALE = 'cs' as const;
const INVOICE_STATUSES: readonly InvoiceStatus[] = ['draft', 'issued', 'paid', 'credit_noted', 'cancelled', 'credit_note'];

const STATUS_LABEL_KEY: Readonly<Record<InvoiceStatus, MessageKey>> = {
  draft: 'webAdmin.fakturaceDoklady.status.draft',
  issued: 'webAdmin.fakturaceDoklady.status.issued',
  paid: 'webAdmin.fakturaceDoklady.status.paid',
  credit_noted: 'webAdmin.fakturaceDoklady.status.credit_noted',
  cancelled: 'webAdmin.fakturaceDoklady.status.cancelled',
  // FIX6 nalez 8c/8d - sam dobropis (novy doklad), ne stav puvodniho dokladu.
  credit_note: 'webAdmin.fakturaceDoklady.status.credit_note',
};

function errorDetailProp(value: string | undefined): { errorDetail?: string } {
  return value === undefined ? {} : { errorDetail: value };
}

function domainErrorMessage(error: unknown): string | undefined {
  return error instanceof DomainApiError ? error.message : undefined;
}

/** Krok PROP3B: prodleva, nez se hledani zakazky zepta serveru (psani adresy). */
const CASE_SEARCH_DEBOUNCE_MS = 250;

function money(amount: string): string {
  const value = Number(amount);
  if (!Number.isFinite(value)) return amount;
  return new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
}

export function InvoiceStatusBadge({ status }: { readonly status: InvoiceStatus }): React.JSX.Element {
  return (
    <span className="rh-badge rh-badge--neutral" data-testid="invoice-status-badge">
      {t(STATUS_LABEL_KEY[status], { locale: LOCALE })}
    </span>
  );
}

// ----------------------------------------------------------------- detail

function InvoiceDetailPanel({
  invoiceId,
  onChanged,
  onClose,
}: {
  readonly invoiceId: string;
  readonly onChanged: () => void;
  readonly onClose?: (() => void) | undefined;
}): React.JSX.Element {
  const [invoice, setInvoice] = React.useState<InvoiceWithItemsDto | null>(null);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState<string | null>(null);
  const [actionError, setActionError] = React.useState<string | undefined>(undefined);
  const [creditNoteReason, setCreditNoteReason] = React.useState('');
  // Krok FAK5 bod 1 - datum SKUTECNE uhrady (ne okamzik kliknuti); vychozi dnes, kancelar ho muze zmenit.
  const [paidOn, setPaidOn] = React.useState(() => todayIso());
  const [paymentsSnapshot, setPaymentsSnapshot] = React.useState<PaymentsSnapshotDto | null>(null);
  // Krok FAK5 bod 2 - "jen cast": vybrane polozky + mnozstvi k dobropisu (vychozi = cele puvodni mnozstvi).
  const [partialCreditNote, setPartialCreditNote] = React.useState(false);
  const [creditSelection, setCreditSelection] = React.useState<Record<string, boolean>>({});
  const [creditQuantities, setCreditQuantities] = React.useState<Record<string, string>>({});

  const reload = React.useCallback(async () => {
    try {
      const loaded = await getInvoice(invoiceId);
      setInvoice(loaded);
      setCreditQuantities(Object.fromEntries(loaded.items.map((item) => [item.id, item.quantity])));
      setLoadError(null);
    } catch (error) {
      setLoadError(domainErrorMessage(error) ?? t('webAdmin.fakturaceDoklady.loadError', { locale: LOCALE }));
    }
  }, [invoiceId]);

  React.useEffect(() => {
    void reload();
  }, [reload]);

  async function run(name: string, action: () => Promise<InvoiceWithItemsDto | void>): Promise<void> {
    setBusy(name);
    setActionError(undefined);
    try {
      const result = await action();
      if (result !== undefined) setInvoice(result);
      else await reload();
      onChanged();
    } catch (error) {
      setActionError(domainErrorMessage(error));
    } finally {
      setBusy(null);
    }
  }

  if (loadError !== null) return <p role="alert" className="text-red-600">{loadError}</p>;
  if (invoice === null) return <p role="status">…</p>;

  return (
    <div className="rh-card p-4" data-testid="invoice-detail">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-bold">
          {invoice.myuctoNumber !== null
            ? t('webAdmin.fakturaceDoklady.detailTitle', { locale: LOCALE, values: { number: invoice.myuctoNumber } })
            : t('webAdmin.fakturaceDoklady.detailTitleDraft', { locale: LOCALE })}
        </h3>
        <InvoiceStatusBadge status={invoice.status} />
      </div>

      <p className="text-sm text-gray-600" data-testid="invoice-detail-customer">
        {t('webAdmin.fakturaceDoklady.customerHeading', { locale: LOCALE })}: {invoice.customer.name}
      </p>
      <p className="text-sm text-gray-600">
        {czDate(invoice.issueDate)} · {czDate(invoice.dueDate)}
      </p>

      <table className="mt-3 w-full text-sm" data-testid="invoice-detail-items">
        <thead>
          <tr className="text-left text-gray-500">
            {partialCreditNote && <th />}
            <th>{t('webAdmin.fakturaceDoklady.itemsColumnDescription', { locale: LOCALE })}</th>
            <th>{t('webAdmin.fakturaceDoklady.itemsColumnQuantity', { locale: LOCALE })}</th>
            <th>{t('webAdmin.fakturaceDoklady.itemsColumnUnitPrice', { locale: LOCALE })}</th>
            <th>{t('webAdmin.fakturaceDoklady.itemsColumnVat', { locale: LOCALE })}</th>
          </tr>
        </thead>
        <tbody>
          {invoice.items.map((item) => (
            <tr key={item.id}>
              {partialCreditNote && (
                <td>
                  <input
                    type="checkbox"
                    checked={creditSelection[item.id] ?? false}
                    onChange={(e) => setCreditSelection((prev) => ({ ...prev, [item.id]: e.target.checked }))}
                    data-testid={`credit-note-item-select-${item.id}`}
                  />
                </td>
              )}
              <td>{item.description}</td>
              <td>
                {partialCreditNote && (creditSelection[item.id] ?? false) ? (
                  <input
                    className="rh-input w-20"
                    value={creditQuantities[item.id] ?? item.quantity}
                    onChange={(e) => setCreditQuantities((prev) => ({ ...prev, [item.id]: e.target.value }))}
                    data-testid={`credit-note-item-quantity-${item.id}`}
                  />
                ) : (
                  czQuantity(item.quantity)
                )}
              </td>
              <td>{money(item.unitPriceWithoutVat)}</td>
              {/* Krok QAFIX9b: sazba jde JEDNIM formatovacem jako na obrazovce
                  „Tarif a platba" — `numeric` ze serveru muze prijit „21.00"
                  a doklad by hlasil „21.00 %". */}
              <td>{formatPercent(item.vatRatePercent)} %</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-3 flex flex-col gap-1 text-sm">
        <p>{t('webAdmin.fakturaceDoklady.subtotalLabel', { locale: LOCALE })}: {money(invoice.subtotalAmount)} Kč</p>
        <p>{t('webAdmin.fakturaceDoklady.vatLabel', { locale: LOCALE })}: {money(invoice.vatAmount)} Kč</p>
        <p className="font-bold">{t('webAdmin.fakturaceDoklady.totalLabel', { locale: LOCALE })}: {money(invoice.totalAmount)} Kč</p>
        {invoice.sentTo !== null && invoice.sentTo.length > 0 && (
          <p data-testid="invoice-detail-sent-to">{t('webAdmin.fakturaceDoklady.sentToLabel', { locale: LOCALE })}: {invoice.sentTo.join(', ')}</p>
        )}
        {invoice.paidAt !== null && <p data-testid="invoice-detail-paid-at">{t('webAdmin.fakturaceDoklady.paidAtLabel', { locale: LOCALE })}: {czDate(invoice.paidAt)}</p>}
        {invoice.cancelReason !== null && <p>{t('webAdmin.fakturaceDoklady.cancelReasonLabel', { locale: LOCALE })}: {invoice.cancelReason}</p>}
        {paymentsSnapshot !== null && (
          <p data-testid="invoice-payments-snapshot">
            {t('webAdmin.fakturaceDoklady.paymentsSnapshotLabel', {
              locale: LOCALE,
              values: { paid: money(paymentsSnapshot.paidTotal), total: money(paymentsSnapshot.amountToPay) },
            })}
            {' · '}
            {t(PAYMENT_STATUS_LABEL_KEY[paymentsSnapshot.paymentStatus ?? 'unpaid'], { locale: LOCALE })}
          </p>
        )}
      </div>

      {actionError !== undefined && (
        <p role="alert" className="mt-2 text-red-600">
          {actionError}
        </p>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        {invoice.status === 'draft' && (
          <>
            <button
              type="button"
              className="rh-btn rh-btn--primary"
              disabled={busy !== null}
              onClick={() => void run('issue', () => issueInvoice(invoice.id))}
              data-testid="invoice-action-issue"
            >
              {t('webAdmin.fakturaceDoklady.actions.issue', { locale: LOCALE })}
            </button>
            <button
              type="button"
              className="rh-btn"
              disabled={busy !== null}
              onClick={() => {
                if (typeof window !== 'undefined' && !window.confirm(t('webAdmin.fakturaceDoklady.deleteConfirm', { locale: LOCALE }))) return;
                void run('delete', async () => {
                  await deleteInvoiceDraft(invoice.id);
                  onClose?.();
                });
              }}
              data-testid="invoice-action-delete"
            >
              {t('webAdmin.fakturaceDoklady.actions.delete', { locale: LOCALE })}
            </button>
          </>
        )}
        {invoice.status !== 'draft' && (
          <button
            type="button"
            className="rh-btn"
            onClick={() => void downloadInvoicePdf(invoice.id, `${invoice.myuctoNumber ?? invoice.id}.pdf`)}
            data-testid="invoice-action-pdf"
          >
            {t('webAdmin.fakturaceDoklady.actions.pdf', { locale: LOCALE })}
          </button>
        )}
        {invoice.status === 'issued' && (
          <>
            <button
              type="button"
              className="rh-btn"
              disabled={busy !== null}
              onClick={() => void run('send', () => sendInvoiceEmail(invoice.id))}
              data-testid="invoice-action-send"
            >
              {t('webAdmin.fakturaceDoklady.actions.send', { locale: LOCALE })}
            </button>
            <label htmlFor="mark-paid-date" className="sr-only">
              {t('webAdmin.fakturaceDoklady.paidOnLabel', { locale: LOCALE })}
            </label>
            <DateField
              id="mark-paid-date"
              className="rh-input"
              value={paidOn}
              onValueChange={setPaidOn}
              testId="invoice-mark-paid-date"
            />
            <button
              type="button"
              className="rh-btn"
              disabled={busy !== null}
              onClick={() => void run('mark-paid', () => markInvoicePaid(invoice.id, paidOn))}
              data-testid="invoice-action-mark-paid"
            >
              {t('webAdmin.fakturaceDoklady.actions.markPaid', { locale: LOCALE })}
            </button>
          </>
        )}
        {
          // FIX6 nalez 8d: dobropis sam neni pohledavka - parovani plateb na nem nema smysl.
          invoice.myuctoNumber !== null &&
            invoice.status !== 'cancelled' &&
            invoice.status !== 'credit_noted' &&
            invoice.status !== 'credit_note' && (
              <button
                type="button"
                className="rh-btn"
                disabled={busy !== null}
                onClick={() =>
                  void run('payments-sync', async () => {
                    const result = await syncInvoicePayment(invoice.id);
                    setPaymentsSnapshot(result.payments);
                    return result.invoice;
                  })
                }
                data-testid="invoice-action-payments-sync"
              >
                {t('webAdmin.fakturaceDoklady.actions.paymentsSync', { locale: LOCALE })}
              </button>
            )
        }
        {(invoice.status === 'issued' || invoice.status === 'paid') && (
          <form
            className="flex flex-wrap items-end gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              const items = partialCreditNote
                ? invoice.items
                    .filter((item) => creditSelection[item.id] ?? false)
                    .map((item) => ({ itemId: item.id, quantity: creditQuantities[item.id] ?? item.quantity }))
                : undefined;
              void run('credit-note', () => issueCreditNote(invoice.id, creditNoteReason, items));
            }}
          >
            <FieldRow label={t('webAdmin.fakturaceDoklady.creditNoteReasonLabel', { locale: LOCALE })} htmlFor="credit-note-reason">
              <input
                id="credit-note-reason"
                required
                className="rh-input"
                value={creditNoteReason}
                onChange={(e) => setCreditNoteReason(e.target.value)}
                data-testid="invoice-credit-note-reason"
              />
            </FieldRow>
            {invoice.status === 'paid' && (
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={partialCreditNote}
                  onChange={(e) => setPartialCreditNote(e.target.checked)}
                  data-testid="invoice-credit-note-partial-toggle"
                />
                {t('webAdmin.fakturaceDoklady.creditNotePartialLabel', { locale: LOCALE })}
              </label>
            )}
            <button
              type="submit"
              className="rh-btn"
              disabled={busy !== null || (partialCreditNote && invoice.items.every((item) => !(creditSelection[item.id] ?? false)))}
              data-testid="invoice-action-credit-note"
            >
              {t(partialCreditNote ? 'webAdmin.fakturaceDoklady.creditNotePartialSubmit' : 'webAdmin.fakturaceDoklady.creditNoteSubmit', { locale: LOCALE })}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------- list

export function InvoicesListTab({ caseId }: { readonly caseId?: string | undefined } = {}): React.JSX.Element {
  const [items, setItems] = React.useState<InvoiceDto[] | null>(null);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [status, setStatus] = React.useState<InvoiceStatus | ''>('');
  const [selectedId, setSelectedId] = React.useState<string | null>(null);

  const reload = React.useCallback(async () => {
    try {
      setItems(await listInvoices({ ...(status !== '' ? { status } : {}), ...(caseId !== undefined ? { caseId } : {}) }));
      setLoadError(null);
    } catch (error) {
      setLoadError(domainErrorMessage(error) ?? t('webAdmin.fakturaceDoklady.loadError', { locale: LOCALE }));
    }
  }, [status, caseId]);

  React.useEffect(() => {
    void reload();
  }, [reload]);

  return (
    <div className="flex flex-col gap-4" data-testid="invoices-list-tab">
      {caseId === undefined && (
        <div className="flex items-center gap-2">
          <label htmlFor="invoices-status-filter" className="text-sm text-gray-600">
            {t('webAdmin.fakturaceDoklady.statusLabel', { locale: LOCALE })}
          </label>
          <select
            id="invoices-status-filter"
            className="rh-input"
            value={status}
            onChange={(e) => setStatus(e.target.value as InvoiceStatus | '')}
            data-testid="invoices-status-filter"
          >
            <option value="">{t('webAdmin.fakturaceDoklady.statusAll', { locale: LOCALE })}</option>
            {INVOICE_STATUSES.map((s) => (
              <option key={s} value={s}>
                {t(STATUS_LABEL_KEY[s], { locale: LOCALE })}
              </option>
            ))}
          </select>
        </div>
      )}

      {loadError !== null && <p role="alert" className="text-red-600">{loadError}</p>}
      {items === null && loadError === null && <p role="status">{t('webAdmin.fakturaceDoklady.loading', { locale: LOCALE })}</p>}
      {items !== null && items.length === 0 && <p data-testid="invoices-empty">{t('webAdmin.fakturaceDoklady.empty', { locale: LOCALE })}</p>}

      {items !== null && items.length > 0 && (
        <table className="w-full text-sm" data-testid="invoices-table">
          <thead>
            <tr className="text-left text-gray-500">
              <th>{t('webAdmin.fakturaceDoklady.columns.issueDate', { locale: LOCALE })}</th>
              <th>{t('webAdmin.fakturaceDoklady.columns.number', { locale: LOCALE })}</th>
              <th>{t('webAdmin.fakturaceDoklady.columns.customer', { locale: LOCALE })}</th>
              <th>{t('webAdmin.fakturaceDoklady.columns.total', { locale: LOCALE })}</th>
              <th>{t('webAdmin.fakturaceDoklady.columns.status', { locale: LOCALE })}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {items.map((invoice) => (
              <tr key={invoice.id} data-testid={`invoice-row-${invoice.id}`}>
                <td>{czDate(invoice.issueDate)}</td>
                <td>{invoice.myuctoNumber ?? '—'}</td>
                <td>{invoice.customer.name}</td>
                <td>{money(invoice.totalAmount)} Kč</td>
                <td><InvoiceStatusBadge status={invoice.status} /></td>
                <td>
                  <button type="button" className="rh-btn" onClick={() => setSelectedId(invoice.id)} data-testid={`invoice-view-${invoice.id}`}>
                    {t('webAdmin.fakturaceDoklady.actions.view', { locale: LOCALE })}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {selectedId !== null && (
        <InvoiceDetailPanel invoiceId={selectedId} onChanged={() => void reload()} onClose={() => setSelectedId(null)} />
      )}
    </div>
  );
}

// ------------------------------------------------------- prehled dokladu (UXR2)

/**
 * Castka bez desetin pro dlazdici — v predloze „74 400 Kč", ne „74 400,00".
 * Krok PROV1 ji pouziva v prehledu provizi (`commission-payouts.tsx`), aby
 * se castky na obrazovkach Penez psaly jednim pravidlem.
 */
export function moneyShort(amount: string, currency: string | null): string {
  const value = Number(amount);
  if (!Number.isFinite(value)) return amount;
  return currency === null
    ? new Intl.NumberFormat('cs-CZ', { maximumFractionDigits: 0 }).format(value)
    : new Intl.NumberFormat('cs-CZ', { style: 'currency', currency, maximumFractionDigits: 0 }).format(value);
}

function czDay(iso: string): string {
  return new Date(iso).toLocaleDateString('cs-CZ', { day: 'numeric', month: 'numeric' });
}

/** FIX6 nalez 8e - plne datum („29. 9. 2026"), ne ISO retezec z API. `iso` smi byt datum i cas (jen den se pouzije). */
function czDate(iso: string): string {
  return new Date(iso.length > 10 ? iso : `${iso}T00:00:00`).toLocaleDateString('cs-CZ', {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
  });
}

/** FIX6 nalez 8e - mnozstvi bez zbytecnych nul za desetinnou carkou ("1.0000" -> "1", "0.5000" -> "0,5"). */
function czQuantity(value: string): string {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return value;
  return new Intl.NumberFormat('cs-CZ', { maximumFractionDigits: 4 }).format(parsed);
}

function Stat({
  label,
  value,
  note,
  bad,
  testId,
}: {
  readonly label: string;
  readonly value: string;
  readonly note: string;
  readonly bad?: boolean;
  readonly testId: string;
}): React.JSX.Element {
  return (
    <div className="rh-a2-stat" data-testid={testId}>
      <div className="rh-a2-stat__label">{label}</div>
      <div className={`rh-a2-stat__value${bad === true ? ' rh-a2-stat__value--bad' : ''}`}>{value}</div>
      <div className="rh-a2-stat__note">{note}</div>
    </div>
  );
}

/**
 * Krok UXR2 — PREHLED FAKTURACE podle predlohy `05-fakturace.html`: tri
 * dlazdice, rychle filtry, hledani a seznam dokladu se zakazkou a splatnosti.
 *
 * Jeden dotaz na doklady (`listInvoices()`) obsluhuje dlazdice, filtry i
 * tabulku — cisla v dlazdicich tak nemohou rict nic jineho nez seznam pod
 * nimi (vypocet je v `lib/invoice-stats.ts`). Nazvy zakazek chodi jednim
 * seznamem (`listCases`), ne dotazem na radek.
 *
 * Detail dokladu zustava `InvoiceDetailPanel` — tedy VSECHNY funkce z FAK5
 * (uhrada s datem, parovani plateb, castecny dobropis) beze zmeny.
 */
export function InvoicesOverview(): React.JSX.Element {
  const [items, setItems] = React.useState<InvoiceDto[] | null>(null);
  const [caseTitles, setCaseTitles] = React.useState<ReadonlyMap<string, string>>(new Map());
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [filter, setFilter] = React.useState<InvoiceFilter>('all');
  const [query, setQuery] = React.useState('');
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const now = React.useMemo(() => systemClock.now(), [items]);

  const reload = React.useCallback(async () => {
    try {
      setItems(await listInvoices({}));
      setLoadError(null);
    } catch (error) {
      setLoadError(domainErrorMessage(error) ?? t('webAdmin.fakturaceDoklady.loadError', { locale: LOCALE }));
    }
  }, []);

  React.useEffect(() => {
    void reload();
  }, [reload]);

  React.useEffect(() => {
    let cancelled = false;
    // Sloupec „Zakázka" z predlohy. Kdyz se seznam zakazek nepovede nacist,
    // sloupec zustane prazdny — doklady se tim nerozbiji.
    void listCases({ limit: 100 }).then(
      (result) => {
        if (!cancelled) setCaseTitles(new Map(result.items.map((item) => [item.id, item.title])));
      },
      () => undefined,
    );
    return () => { cancelled = true; };
  }, []);

  const all = items ?? [];
  const stats = invoiceStats(all, now);
  const counts = invoiceFilterCounts(all, now);
  const rows = filterInvoices(all, filter, query, now);

  const FILTERS: readonly { readonly id: InvoiceFilter; readonly label: string }[] = [
    { id: 'all', label: t('webAdmin.fakturaceV2.filterAll', { locale: LOCALE }) },
    { id: 'due', label: t('webAdmin.fakturaceV2.filterDue', { locale: LOCALE }) },
    { id: 'overdue', label: t('webAdmin.fakturaceV2.filterOverdue', { locale: LOCALE }) },
    { id: 'paid', label: t('webAdmin.fakturaceV2.filterPaid', { locale: LOCALE }) },
    // Audit UXK4 (5/10) — koncepty byly v „Vše" bez vlastniho chipu, nedaly
    // se odfiltrovat samostatne.
    { id: 'draft', label: t('webAdmin.fakturaceV2.filterDraft', { locale: LOCALE }) },
    // Krok UXK3 bod 1 — stornovane doklady jsou mimo "all", takze potrebuji
    // vlastni chip, jinak by se k nim nedalo dostat vubec.
    { id: 'cancelled', label: t('webAdmin.fakturaceV2.filterCancelled', { locale: LOCALE }) },
  ];

  return (
    <div data-testid="invoices-overview">
      {/*
        Krok UXF5 bod 3 (nalez A3): pod 480 px jsou tri dlazdice JEDEN pruh
        („K úhradě · Po splatnosti · Zaplaceno"), aby seznam dokladu zacinal
        jeste v prvnim viewportu — do UXF5 zabraly cely (dukazni snimek E06).
        Sazbu resi `.rh-a2-stats--compact` v `admin-v2.css`; DOM zustava jeden,
        aby `billing-stat-*` nebyly v obrazovce dvakrat.
      */}
      <div className="rh-a2-stats rh-a2-stats--compact" data-testid="billing-stats">
        <Stat
          testId="billing-stat-due"
          label={t('webAdmin.fakturaceV2.statDueLabel', { locale: LOCALE })}
          value={stats.due.total === null ? '—' : moneyShort(stats.due.total, stats.due.currency)}
          note={
            stats.due.nearestDueDate === null
              ? t('webAdmin.fakturaceV2.statDueNoteEmpty', { locale: LOCALE })
              : t('webAdmin.fakturaceV2.statDueNote', {
                  locale: LOCALE,
                  values: { count: stats.due.count, date: czDay(stats.due.nearestDueDate) },
                })
          }
        />
        <Stat
          testId="billing-stat-overdue"
          label={t('webAdmin.fakturaceV2.statOverdueLabel', { locale: LOCALE })}
          value={stats.overdue.total === null ? '—' : moneyShort(stats.overdue.total, stats.overdue.currency)}
          bad={stats.overdue.count > 0}
          note={
            stats.overdue.count === 0
              ? t('webAdmin.fakturaceV2.statOverdueNoteEmpty', { locale: LOCALE })
              : t('webAdmin.fakturaceV2.statOverdueNote', {
                  locale: LOCALE,
                  values: { count: stats.overdue.count, days: stats.overdue.maxDaysLate },
                })
          }
        />
        {/*
          Predloha ma „Zaplaceno v září". Nazev mesice z `Intl` je ale 1. pad
          („říjen"), takze „Zaplaceno v říjen" by byla hruba chyba — a druhy
          seznam mesicu v 6. padu jen kvuli jednomu popisku je zbytecny slovnik
          navic. Zadani UXR2 mluvi o „zaplaceno tento měsíc", drzime jeho zneni.
        */}
        <Stat
          testId="billing-stat-paid"
          label={t('webAdmin.fakturaceV2.statPaidLabel', { locale: LOCALE })}
          value={stats.paidThisMonth.total === null ? '—' : moneyShort(stats.paidThisMonth.total, stats.paidThisMonth.currency)}
          note={
            stats.paidThisMonth.lastPaidAt === null
              ? t('webAdmin.fakturaceV2.statPaidNoteEmpty', { locale: LOCALE })
              : t('webAdmin.fakturaceV2.statPaidNote', {
                  locale: LOCALE,
                  values: { count: stats.paidThisMonth.count, date: czDay(stats.paidThisMonth.lastPaidAt) },
                })
          }
        />
      </div>

      <div className="rh-a2-filters">
        {FILTERS.map((item) => (
          <button
            key={item.id}
            type="button"
            className={`rh-a2-chip${filter === item.id ? ' is-on' : ''}`}
            aria-pressed={filter === item.id}
            onClick={() => setFilter(item.id)}
            data-testid={`invoices-filter-${item.id}`}
          >
            {item.label} <span className="rh-a2-chip__n">{counts[item.id]}</span>
          </button>
        ))}
        <div className="rh-a2-field rh-a2-filters__right">
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('webAdmin.fakturaceV2.searchPlaceholder', { locale: LOCALE })}
            aria-label={t('webAdmin.fakturaceV2.searchAriaLabel', { locale: LOCALE })}
            data-testid="invoices-search"
            style={{ width: 260 }}
          />
        </div>
      </div>

      {loadError !== null && <p role="alert" className="rh-a2-error">{loadError}</p>}
      {items === null && loadError === null && (
        <p role="status" className="rh-a2-status">{t('webAdmin.fakturaceDoklady.loading', { locale: LOCALE })}</p>
      )}
      {items !== null && rows.length === 0 && (
        <div className="rh-a2-empty" data-testid="invoices-empty">
          <p className="rh-a2-empty__title">{t('webAdmin.fakturaceDoklady.empty', { locale: LOCALE })}</p>
        </div>
      )}

      {rows.length > 0 && (
        <div className="rh-a2-tbl-wrap">
          <table className="rh-a2-tbl" data-testid="invoices-table">
            <thead>
              <tr>
                <th>{t('webAdmin.fakturaceDoklady.columns.number', { locale: LOCALE })}</th>
                <th style={{ width: '22%' }}>{t('webAdmin.fakturaceDoklady.columns.customer', { locale: LOCALE })}</th>
                <th style={{ width: '22%' }}>{t('webAdmin.fakturaceV2.columnCase', { locale: LOCALE })}</th>
                <th className="rh-a2-r">{t('webAdmin.fakturaceDoklady.columns.total', { locale: LOCALE })}</th>
                <th className="rh-a2-nowrap">{t('webAdmin.fakturaceV2.columnDueDate', { locale: LOCALE })}</th>
                <th>{t('webAdmin.fakturaceDoklady.columns.status', { locale: LOCALE })}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((invoice) => {
                const late = daysLate(invoice, now);
                const isOverdue = invoice.status === 'issued' && late > 0;
                return (
                  /*
                    Krok FIX6 bod 2: detail otevira CELY radek, ne jen cislo
                    dokladu — klik na odberatele nebo castku driv neudelal nic
                    (nalez ze ziveho overeni 2. 10.). Tlacitko na cisle
                    ZUSTAVA: je to pristupny ovladac (klavesnice, ctecka),
                    radek je jen zkratka pro mys.
                  */
                  <tr
                    key={invoice.id}
                    data-testid={`invoice-row-${invoice.id}`}
                    className="rh-a2-tbl__row--open"
                    onClick={() => setSelectedId(invoice.id)}
                  >
                    <td data-label={t('webAdmin.fakturaceDoklady.columns.number', { locale: LOCALE })}>
                      <button
                        type="button"
                        className="rh-a2-strong rh-a2-num"
                        onClick={() => setSelectedId(invoice.id)}
                        data-testid={`invoice-view-${invoice.id}`}
                      >
                        {/*
                          Cislo prideluje az ucetni system. VYSTAVENY doklad
                          bez cisla proto NENI koncept — nazvat ho tak by o nem
                          lhalo (nalez ze ziveho overeni UXR2); stav nese
                          vlastni sloupec.
                        */}
                        {invoice.myuctoNumber ?? t('webAdmin.fakturaceV2.noNumber', { locale: LOCALE })}
                      </button>
                      <span className="rh-a2-sub">
                        {t(`webAdmin.fakturaceDoklady.source.${invoice.source}` as MessageKey, { locale: LOCALE })}
                      </span>
                    </td>
                    <td data-label={t('webAdmin.fakturaceDoklady.columns.customer', { locale: LOCALE })}>
                      {invoice.customer.name}
                      {/* Krok TOKYFIX4 bod 1: PREDMET dokladu. Koncept nema
                          cislo, takze bez predmetu byly dva koncepty tehoz
                          odberatele na obrazovce k nerozeznani (mereni P7). */}
                      {invoice.subject !== null && invoice.subject !== undefined && invoice.subject !== '' && (
                        <span className="rh-a2-sub" data-testid={`invoice-subject-${invoice.id}`}>
                          {invoice.subject}
                        </span>
                      )}
                    </td>
                    <td data-label={t('webAdmin.fakturaceV2.columnCase', { locale: LOCALE })}>
                      {invoice.caseId === null
                        ? t('webAdmin.fakturaceV2.noCase', { locale: LOCALE })
                        : (caseTitles.get(invoice.caseId) ?? t('webAdmin.fakturaceV2.noCase', { locale: LOCALE }))}
                    </td>
                    <td
                      className="rh-a2-r rh-a2-num rh-a2-nowrap"
                      style={isOverdue ? { color: 'var(--rh-danger)', fontWeight: 600 } : { fontWeight: 600 }}
                      data-label={t('webAdmin.fakturaceDoklady.columns.total', { locale: LOCALE })}
                    >
                      {money(invoice.totalAmount)} Kč
                    </td>
                    <td className="rh-a2-nowrap rh-a2-num" data-label={t('webAdmin.fakturaceV2.columnDueDate', { locale: LOCALE })}>
                      {czDate(invoice.dueDate)}
                    </td>
                    <td data-label={t('webAdmin.fakturaceDoklady.columns.status', { locale: LOCALE })}>
                      {isOverdue ? (
                        <span className="rh-a2-badge rh-a2-badge--bad">
                          {t('webAdmin.fakturaceV2.overdueBadge', { locale: LOCALE, values: { days: late } })}
                        </span>
                      ) : invoice.status === 'paid' ? (
                        <span className="rh-a2-badge rh-a2-badge--ok">
                          {invoice.paidAt === null
                            ? t('webAdmin.fakturaceDoklady.status.paid', { locale: LOCALE })
                            : t('webAdmin.fakturaceV2.paidBadge', { locale: LOCALE, values: { date: czDay(invoice.paidAt) } })}
                        </span>
                      ) : invoice.status === 'issued' ? (
                        <span className="rh-a2-badge rh-a2-badge--wait">
                          {t('webAdmin.fakturaceV2.dueBadge', { locale: LOCALE })}
                        </span>
                      ) : (
                        <span className="rh-a2-badge">{t(STATUS_LABEL_KEY[invoice.status], { locale: LOCALE })}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="rh-a2-meta" style={{ marginTop: 12 }}>
        {t('webAdmin.fakturaceV2.foot', { locale: LOCALE })}
      </p>

      {selectedId !== null && (
        <InvoiceDetailPanel invoiceId={selectedId} onChanged={() => void reload()} onClose={() => setSelectedId(null)} />
      )}
    </div>
  );
}

// -------------------------------------------------------------- new invoice

interface ItemForm {
  catalogItemId: string | null;
  description: string;
  quantity: string;
  unitPriceWithoutVat: string;
  vatRatePercent: string;
}

function emptyItem(catalog: CatalogItemDto | undefined): ItemForm {
  return {
    catalogItemId: catalog?.id ?? null,
    description: catalog?.name ?? '',
    quantity: '1',
    unitPriceWithoutVat: catalog?.defaultUnitPrice ?? '',
    vatRatePercent: catalog?.vatRatePercent ?? '21',
  };
}

/**
 * Krok APP2 bod 4 (bod 25 zadani) — JE NA NOVE FAKTURE CO ZACHRANOVAT?
 *
 * „Nová faktura" neuklada NIC, dokud se doklad nevystavi: obnoveni stranky
 * nebo zavreni zalozky zahodi odberatele, radky i provizi. Hlidka
 * (`useLeaveWarning`) proto musi vedet, kdy uz makler neco vyplnil — a kdy
 * jeste ne, aby neotravovala nad prazdnym formularem.
 *
 * Vyplnena je POUZE HODNOTA, kterou zadal clovek. Predvolby (datum vystaveni
 * a splatnosti, jednotkove mnozstvi „1", sazba DPH) rozpracovana prace
 * nejsou — presne jako `wizardHasWork` u nove zakazky (krok KLIKFIX1).
 * Exportovano kvuli meritelnosti bez vykresleni cele obrazovky.
 */
export function newInvoiceHasWork(form: {
  readonly customer: InvoiceCustomerDto;
  readonly items: readonly ItemForm[];
  readonly caseId: string;
  readonly partyId: string | null;
  readonly basisAmount: string;
  readonly commissionValue: string;
  readonly commissionDescription: string;
}): boolean {
  const vyplneno = (value: string | null | undefined): boolean => (value ?? '').trim() !== '';
  if (
    vyplneno(form.customer.name) ||
    vyplneno(form.customer.street) ||
    vyplneno(form.customer.city) ||
    vyplneno(form.customer.zip) ||
    vyplneno(form.customer.email) ||
    vyplneno(form.customer.ico) ||
    vyplneno(form.customer.dic)
  ) {
    return true;
  }
  if (form.partyId !== null || vyplneno(form.caseId)) return true;
  if (vyplneno(form.basisAmount) || vyplneno(form.commissionValue) || vyplneno(form.commissionDescription)) {
    return true;
  }
  return form.items.some((item) => vyplneno(item.description) || vyplneno(item.unitPriceWithoutVat));
}

function todayIso(): string {
  return systemClock.now().toISOString().slice(0, 10);
}

function inDaysIso(days: number): string {
  const d = systemClock.now();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

const EMPTY_CUSTOMER: InvoiceCustomerDto = { name: '', ico: null, dic: null, street: '', city: '', zip: '', email: null };

/**
 * Krok FAK1 bod 1: u kazde prevzate hodnoty stoji, ODKUD je — makler jinak
 * nevi, jestli adresu na dokladu nekdo overil, nebo ji jen nekdo kdysi opsal.
 */
const PREFILL_SOURCE_KEY: Readonly<Record<CustomerPrefillSourceDto, MessageKey>> = {
  'party-card': 'webAdmin.fakturaceNova.prefillSourcePartyCard',
  'party-contact': 'webAdmin.fakturaceNova.prefillSourcePartyContact',
  'party-tax-id': 'webAdmin.fakturaceNova.prefillSourcePartyTaxId',
};

/**
 * Krok P8F8 bod 2: ze ceho NABIDKA provize je. Zdroj `asking-price` tu uz
 * neni — doklad nabidkovou cenu necte (R.B. 27. 10.: provize se meni, system
 * ji nepocita).
 */
const OFFER_SOURCE_KEY: Readonly<Record<CommissionOfferSource, MessageKey>> = {
  'contract-commission': 'webAdmin.fakturaceNova.offerSourceContractCommission',
  'contract-price': 'webAdmin.fakturaceNova.offerSourceContractPrice',
};

/** Krok KLIKFIX1 (vada V5): veta ke kazdemu varovani u provize. */
const COMMISSION_WARNING_KEY: Readonly<Record<CommissionWarning, MessageKey>> = {
  'vat-over-contract': 'webAdmin.fakturaceNova.commissionWarnVatOverContract',
  'amount-too-small': 'webAdmin.fakturaceNova.commissionWarnAmountTooSmall',
};

function prefillSourceNote(source: CustomerPrefillSourceDto | null | undefined): string | null {
  if (source === null || source === undefined) return null;
  return t(PREFILL_SOURCE_KEY[source], { locale: LOCALE });
}

/** Adresa, kterou MyUcto prijme — tytez tri pole hlida `validateCustomer` na serveru. */
function customerAddressComplete(customer: InvoiceCustomerDto): boolean {
  return customer.street.trim() !== '' && customer.city.trim() !== '' && customer.zip.trim() !== '';
}

interface AmountSummary {
  readonly base: number;
  readonly vat: number;
  readonly total: number;
}

/** `Number('')` je 0, ne NaN - prazdna polozka proto pocita jako 0 Kc, ne jako chyba (krok UXK2). */
function toAmount(value: string): number {
  const n = Number(value.replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
}

/** Radek polozky = mnozstvi * cena bez DPH; souhrn sečte vsechny radky a jejich DPH. */
function lineBase(item: ItemForm): number {
  return toAmount(item.quantity) * toAmount(item.unitPriceWithoutVat);
}

function freeItemsSummary(items: readonly ItemForm[]): AmountSummary {
  let base = 0;
  let vat = 0;
  for (const item of items) {
    const line = lineBase(item);
    base += line;
    vat += line * (toAmount(item.vatRatePercent) / 100);
  }
  return { base, vat, total: base + vat };
}

function commissionSummary(
  basisAmount: string,
  commissionType: 'percent' | 'fixed',
  commissionValue: string,
  commissionVat: string,
): AmountSummary {
  const base = commissionType === 'percent' ? toAmount(basisAmount) * (toAmount(commissionValue) / 100) : toAmount(commissionValue);
  const vat = base * (toAmount(commissionVat) / 100);
  return { base, vat, total: base + vat };
}

export function NewInvoiceTab({
  supplierId,
  vatPayer,
  initialCaseId,
  onCancel,
}: {
  readonly supplierId: string | null;
  readonly vatPayer: boolean;
  readonly initialCaseId?: string | undefined;
  /**
   * Krok UXP4a bod 3 (nalez P2-3): ZAVRIT formular. Driv tu bylo jeste
   * `onCreated`, kterym volajici po ulozeni formular zavrel — potvrzeni
   * („Faktura je vystavena" + detail dokladu) se proto nikdy neukazalo a
   * makler se jen vratil do seznamu. Zavira se TED az na prani uzivatele.
   */
  readonly onCancel?: (() => void) | undefined;
}): React.JSX.Element {
  const [mode, setMode] = React.useState<'free' | 'commission'>(initialCaseId !== undefined ? 'commission' : 'free');
  const [partyId, setPartyId] = React.useState<string | null>(null);
  const [partyQuery, setPartyQuery] = React.useState('');
  const [partyResults, setPartyResults] = React.useState<PartyDto[]>([]);
  const [customer, setCustomer] = React.useState<InvoiceCustomerDto>(EMPTY_CUSTOMER);
  /** Krok FAK1 bod 1: zdroj kazde prevzate hodnoty odberatele („z adresáře klienta"…). */
  const [prefillSources, setPrefillSources] = React.useState<PartyPrefillDto['sources'] | null>(null);
  /** Krok UXF3 bod 3: prebrane udaje odberatele jsou prehled; tohle je „Upravit udaje". */
  const [customerEditing, setCustomerEditing] = React.useState(false);
  /** V8: fokus na „Ulice" smi prijit az PO prekresleni, ktere pole vubec vyrobi. */
  const [focusStreetPending, setFocusStreetPending] = React.useState(false);
  React.useEffect(() => {
    if (!customerEditing || !focusStreetPending) return;
    focusField('new-invoice-customer-street');
    setFocusStreetPending(false);
  }, [customerEditing, focusStreetPending]);
  const [caseId, setCaseId] = React.useState(initialCaseId ?? '');
  const [caseContext, setCaseContext] = React.useState<CaseInvoiceContextDto | null>(null);
  /**
   * Krok UXP1 (nalez P1-7): hledani zakazky podle adresy/nazvu. Nazvy skladá
   * TATAZ funkce jako „Nová smlouva" (`caseDisplayName` v
   * `lib/case-address-lookup.ts`), takze obe obrazovky nabizeji stejna jmena.
   *
   * Krok PROP3B (oprava e2e 1): hleda se NA SERVERU (`searchCasesByName`).
   * Stahovat cely seznam zakazek a hledat v prohlizeci prestalo v DEMO
   * kancelari fungovat uplne — 54 dotazu, ~6 minut, posledni 429 a prazdna
   * nabidka bez vysvetleni (e2e S18c).
   */
  const [caseQuery, setCaseQuery] = React.useState('');
  const [caseIndex, setCaseIndex] = React.useState<ReadonlyMap<string, string> | null>(null);
  /** Proc nabidka zakazek nic nenabizi (kvota, vypadek) — nikdy tise prazdna. */
  const [caseSearchError, setCaseSearchError] = React.useState<string | null>(null);
  const [saveAsClient, setSaveAsClient] = React.useState(true);
  const [items, setItems] = React.useState<ItemForm[]>([emptyItem(undefined)]);
  const [catalog, setCatalog] = React.useState<CatalogItemDto[]>([]);
  const [basisAmount, setBasisAmount] = React.useState('');
  /**
   * Krok P8F8 bod 2: nabidky provize z PODEPSANYCH smluv zakazky. Nic se
   * nepredvyplnuje — pole „Provize" zacina prazdne a makler nabidku prevezme
   * klepnutim (nebo zada vlastni castku).
   */
  const [commissionOffers, setCommissionOffers] = React.useState<readonly CommissionOffer[]>([]);
  /** Nabidka, kterou makler uz prevzal — radek pak rika, odkud castka je. */
  const [takenOffer, setTakenOffer] = React.useState<CommissionOffer | null>(null);
  const [commissionType, setCommissionType] = React.useState<'percent' | 'fixed'>('percent');
  const [commissionValue, setCommissionValue] = React.useState('');
  const [commissionDescription, setCommissionDescription] = React.useState('');
  const [commissionVat, setCommissionVat] = React.useState(vatPayer ? '21' : '0');
  const [issueDate, setIssueDate] = React.useState(todayIso());
  const [dueDate, setDueDate] = React.useState(inDaysIso(14));
  const [language, setLanguage] = React.useState<'cs' | 'en'>('cs');
  const [status, setStatus] = React.useState<SaveStatusKind>('idle');
  const [errorDetail, setErrorDetail] = React.useState<string | undefined>(undefined);
  const [created, setCreated] = React.useState<InvoiceWithItemsDto | null>(null);

  /*
   * Krok APP2 bod 4 (bod 25): odchod z rozpracovane faktury se ohlasi.
   * Jakmile je doklad VYSTAVENY (`created`), uz neni co zachranovat — data
   * jsou na serveru a makler jen cte potvrzeni.
   */
  useLeaveWarning(
    created === null &&
      newInvoiceHasWork({ customer, items, caseId, partyId, basisAmount, commissionValue, commissionDescription }),
  );

  React.useEffect(() => {
    void listCatalogItems().then(setCatalog, () => setCatalog([]));
  }, []);

  React.useEffect(() => {
    if (partyQuery.trim() === '') {
      setPartyResults([]);
      return;
    }
    const timeout = window.setTimeout(() => {
      void listParties({ q: partyQuery, limit: 10 }).then(
        (result) => setPartyResults(result.items),
        () => setPartyResults([]),
      );
    }, 250);
    return () => window.clearTimeout(timeout);
  }, [partyQuery]);

  /**
   * Krok FAK1 bod 1 (vada V8): prebira se CELY odberatel — adresa a IČO/DIČ
   * z adresare, ne jen jmeno a e-mail. Jedna funkce pro oba vstupy (hledani
   * klienta i strana zakazky), aby se obe cesty nerozesly.
   */
  async function applyPartyPrefill(id: string, fallbackName: string | null): Promise<void> {
    try {
      const prefill = await prefillCustomerFromParty(id);
      setPrefillSources(prefill.sources);
      setCustomer((prev) => ({
        ...prev,
        name: prefill.name,
        email: prefill.email,
        street: prefill.street,
        city: prefill.city,
        zip: prefill.zip,
        ico: prefill.ico,
        dic: prefill.dic,
      }));
    } catch {
      setPrefillSources(null);
      // `null` = volajici zadne nahradni jmeno nema; rozepsane se nemaze.
      setCustomer((prev) => ({ ...prev, name: fallbackName ?? prev.name }));
    }
  }

  async function selectParty(party: PartyDto): Promise<void> {
    setPartyId(party.id);
    setPartyQuery(party.displayName);
    setPartyResults([]);
    // Nove prebrane udaje se ukazuji jako prehled, ne jako rozepsany formular (UXF3 bod 3).
    setCustomerEditing(false);
    await applyPartyPrefill(party.id, party.displayName);
  }

  /*
    Krok UXP1 (nalez P1-7): faktura otevrena z detailu zakazky si kontext
    (nazev a strany) nacte SAMA — makler nemusi mackat „Načíst ze zakázky",
    aby vubec zjistil, proc je „Uložit koncept" sede.
  */
  React.useEffect(() => {
    if (initialCaseId === undefined) return;
    // `loadCase` cte `caseId` ze stavu, ktery `initialCaseId` nastavil uz v `useState`.
    void loadCase();
  }, [initialCaseId]);

  /*
   * Krok PROP3B (oprava e2e 1): hledani bezi na SERVERU a az od dvou znaku —
   * jeden dotaz misto skenu cele kancelare. Kratka prodleva necha makléře
   * dopsat adresu, nez se zepta.
   */
  React.useEffect(() => {
    if (mode !== 'commission' || initialCaseId !== undefined) return;
    const q = caseQuery.trim();
    if (q.length < CASE_SEARCH_MIN_LENGTH) {
      setCaseIndex(null);
      setCaseSearchError(null);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      void retryOnRateLimit(() => searchCasesByName(q)).then(
        (found) => { if (!cancelled) setCaseIndex(found); },
        (error: unknown) => {
          // Kvota nesmi vypadat jako „zakazka neexistuje": zadny prazdny
          // seznam, ale duvod (nalez z e2e S18c — nabidka mlcela).
          if (cancelled) return;
          setCaseIndex(null);
          setCaseSearchError(
            rateLimitMessage(error) ??
              domainErrorMessage(error) ??
              t('webAdmin.fakturaceDoklady.loadError', { locale: LOCALE }),
          );
        },
      );
    }, CASE_SEARCH_DEBOUNCE_MS);
    setCaseSearchError(null);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [mode, initialCaseId, caseQuery]);

  async function loadCase(): Promise<void> {
    if (caseId.trim() === '') return;
    try {
      const context = await getCaseInvoiceContext(caseId.trim());
      setCaseContext(context);
      setCommissionDescription((prev) => (prev === '' ? context.title : prev));
      await loadCommissionOffers(context);
    } catch (error) {
      setErrorDetail(domainErrorMessage(error));
    }
  }

  /**
   * Krok P8F8 bod 2 — PROVIZI ZADAVA MAKLER, SYSTEM JI JEN NABIDNE.
   *
   * Historie: UXP5 bod 7 predvyplnil zaklad z nabidkove ceny nemovitosti,
   * FAK1 bod 2 to zmenil na sjednanou cenu/provizi ze smlouvy — a porad se
   * PREDVYPLNOVALO. R.B. 27. 10. 11:01: provize se meni (prodá se za jinou
   * cenu, sleva, dohoda), takze ji system NEPOCITA a NEPREDVYPLNUJE; pole
   * zacina prazdne. Z PODEPSANE smlouvy se jen NABIDNE castka ke prevzeti
   * (`commissionOffersFromContracts`), koncept smlouvy nenabizi nic a
   * nabidkova cena nemovitosti se uz necte vubec.
   */
  async function loadCommissionOffers(context: CaseInvoiceContextDto): Promise<void> {
    try {
      setCommissionOffers(commissionOffersFromContracts(await listContracts({ caseId: context.caseId })));
    } catch {
      // Smlouvy se nepodarilo precist — makler provizi zada sam, jako bez smluv.
      setCommissionOffers([]);
    }
  }

  /**
   * Krok P8F8 bod 2: klepnuti „Použít provizi ze smlouvy". Sjednana provize
   * je PEVNA CASTKA (nic se nepocita); sjednana cena jde do pomocne
   * kalkulacky jako zaklad a sazbu zada makler.
   */
  function takeCommissionOffer(offer: CommissionOffer): void {
    if (offer.source === 'contract-commission') {
      setCommissionType('fixed');
      setCommissionValue(offer.amount);
      setBasisAmount(offer.amount);
    } else {
      setBasisAmount(offer.amount);
    }
    setTakenOffer(offer);
  }

  /** Vybrana zakazka z napovedy — id do `caseId`, jmeno do pole, a hned nacteni. */
  async function pickCase(id: string, name: string): Promise<void> {
    setCaseId(id);
    setCaseQuery(name);
    setCaseContext(null);
    try {
      const context = await getCaseInvoiceContext(id);
      setCaseContext(context);
      setCommissionDescription((prev) => (prev === '' ? context.title : prev));
      await loadCommissionOffers(context);
      // Jediny prodavajici/pronajimatel = odberatel; makler nic nehleda znovu.
      const party = soleInvoiceParty(context.participants);
      if (party !== null) selectParticipant(party);
    } catch (error) {
      setErrorDetail(domainErrorMessage(error));
    }
  }

  function selectParticipant(p: { readonly partyId: string; readonly role: string }): void {
    setPartyId(p.partyId);
    void applyPartyPrefill(p.partyId, null);
  }

  function updateItem(index: number, patch: Partial<ItemForm>): void {
    setItems((prev) => prev.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  }

  /** Volna faktura bez vybrane karty klienta (ADM1 bod 1a) - na prani zalozi party z rucne vyplnenych udaju. */
  async function resolvePartyIdForFreeInvoice(): Promise<string | null> {
    if (partyId !== null || !saveAsClient) return partyId;
    const contacts: { kind: 'email' | 'phone' | 'address'; value: string }[] = [];
    if (customer.email !== null && customer.email.trim() !== '') contacts.push({ kind: 'email', value: customer.email.trim() });
    const address = [customer.street, customer.zip, customer.city].filter((v) => v.trim() !== '').join(', ').slice(0, 100);
    if (address !== '') contacts.push({ kind: 'address', value: address });
    const { party } = await createParty({
      kind: customer.ico !== null && customer.ico.trim() !== '' ? 'organization' : 'person',
      displayName: customer.name,
      ...(contacts.length > 0 ? { contacts } : {}),
    });
    if (customer.ico !== null && customer.ico.trim() !== '') {
      await addPartyTaxIdentifier(party.id, { country: 'CZ', type: 'CRN', value: customer.ico.trim() });
    }
    if (customer.dic !== null && customer.dic.trim() !== '') {
      await addPartyTaxIdentifier(party.id, { country: 'CZ', type: 'VAT', value: customer.dic.trim() });
    }
    return party.id;
  }

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    if (supplierId === null) return;
    if (mode === 'commission' && partyId === null) return;
    setStatus('saving');
    setErrorDetail(undefined);
    try {
      const result =
        mode === 'free'
          ? await createFreeInvoice({
              supplierId,
              partyId: await resolvePartyIdForFreeInvoice(),
              caseId: caseId.trim() === '' ? null : caseId.trim(),
              customer,
              language,
              issueDate,
              dueDate,
              items: items.map((item) => ({
                ...(item.catalogItemId !== null ? { catalogItemId: item.catalogItemId } : {}),
                description: item.description,
                quantity: item.quantity,
                unitPriceWithoutVat: item.unitPriceWithoutVat,
                vatRatePercent: item.vatRatePercent,
              })),
            })
          : await createCommissionInvoice({
              supplierId,
              // Nenulovost hlida guard "mode === 'commission' && partyId === null" na zacatku submit().
              partyId: partyId as string,
              caseId: caseId.trim(),
              customer,
              language,
              issueDate,
              dueDate,
              basisAmount,
              commission: { type: commissionType, value: commissionValue },
              description: commissionDescription,
              vatRatePercent: commissionVat,
            });
      setCreated(result);
      setStatus('saved');
    } catch (error) {
      setStatus('error');
      setErrorDetail(domainErrorMessage(error));
      // Zivy pruchod 13. 10. (V8): chybejici adresa odberatele davala jen
      // obecnou 422 - makler navic nemel ani KAM ji doplnit, protoze prebrany
      // klient ukazuje jen needitovatelny prehled. Chyba pole proto rovnou
      // otevre editaci a zamiri na "Ulice".
      if (
        error instanceof DomainApiError &&
        error.type !== undefined &&
        kodChyby(error.type) === 'finance/invoice-customer-address-missing'
      ) {
        setCustomerEditing(true);
        setFocusStreetPending(true);
      }
    }
  }

  if (supplierId === null) {
    return (
      <div className="rh-card p-4" data-testid="new-invoice-no-supplier">
        <p className="font-semibold">{t('webAdmin.fakturaceNova.noSupplierTitle', { locale: LOCALE })}</p>
        <p className="text-sm text-gray-600">{t('webAdmin.fakturaceNova.noSupplierDetail', { locale: LOCALE })}</p>
      </div>
    );
  }

  if (created !== null) {
    return (
      <div className="flex flex-col gap-4" data-testid="new-invoice-created">
        <div className="rh-card p-4">
          <p className="font-semibold" role="status">{t('webAdmin.fakturaceNova.createdHeading', { locale: LOCALE })}</p>
          <p className="text-sm text-gray-600">{t('webAdmin.fakturaceNova.createdDetail', { locale: LOCALE })}</p>
          {onCancel !== undefined && (
            <button type="button" className="rh-a2-btn mt-2" onClick={onCancel} data-testid="new-invoice-created-back">
              {t('webAdmin.fakturaceNova.createdBackButton', { locale: LOCALE })}
            </button>
          )}
        </div>
        <InvoiceDetailPanel invoiceId={created.id} onChanged={() => {}} />
      </div>
    );
  }

  const summary = mode === 'free' ? freeItemsSummary(items) : commissionSummary(basisAmount, commissionType, commissionValue, commissionVat);
  const blockedReason = commissionBlockedReason({ mode, caseId, caseContext, partyId });
  // Krok KLIKFIX1 (vada V5): DPH nad provizi ze smlouvy a nesmyslne mala provize.
  const commissionNotes =
    mode === 'free'
      ? []
      : commissionWarnings({
          commissionType,
          basisAmount,
          commissionValue,
          vatRatePercent: commissionVat,
          takenOffer: takenOffer === null ? null : { source: takenOffer.source },
        });

  return (
    <form onSubmit={(e) => void submit(e)} data-testid="new-invoice-form">
      <FormLayout
        width="limited"
        wide={
          mode === 'free' ? (
                  <FormSection title={t('webAdmin.fakturaceNova.itemsHeading', { locale: LOCALE })}>
                    <div className="rh-a2-tbl-wrap rh-a2-tbl-wrap--scroll">
                      <table className="rh-a2-tbl rh-a2-tbl--invoice-items" data-testid="new-invoice-items-table">
                        {/*
                          Krok UXF3 bod 3 (#9): POPIS je nejsirsi sloupec, mnozstvi,
                          cena, DPH a radkovy soucet jen tak uzke, kolik potrebuji.
                          Sirky jsou v `colgroup`, aby je nemusel resit kazdy `td`
                          zvlast (a aby je na mobilu `.rh-a2-tbl` karta ignorovala).

                          Krok UXP4a bod 3 (nalez P2-3): sirky jsou v PIXELECH, ne
                          v procentech. Tabulka stoji v levem sloupci `FormLayout`
                          vedle souhrnu, takze 8 % vyslo na 1440 px na 26 px —
                          z „1000" bylo videt „100(" a zahlavi „Množství" a „Cena
                          bez DPH" se prekryvalo. Pevne minimum + vodorovne
                          rolovani obalu (`--scroll`) drzi pole citelna v kazde
                          sirce sloupce.
                        */}
                        <colgroup>
                          <col style={{ width: '140px' }} />
                          <col style={{ minWidth: '170px' }} />
                          <col style={{ width: '115px' }} />
                          <col style={{ width: '140px' }} />
                          <col style={{ width: '115px' }} />
                          <col style={{ width: '130px' }} />
                          <col style={{ width: '100px' }} />
                        </colgroup>
                        <thead>
                          <tr>
                            <th>{t('webAdmin.fakturaceNova.itemCatalogLabel', { locale: LOCALE })}</th>
                            <th>{t('webAdmin.fakturaceNova.itemDescriptionLabel', { locale: LOCALE })}</th>
                            <th>{t('webAdmin.fakturaceNova.itemQuantityLabel', { locale: LOCALE })}</th>
                            <th>{t('webAdmin.fakturaceNova.itemUnitPriceLabel', { locale: LOCALE })}</th>
                            <th>{t('webAdmin.fakturaceNova.itemVatLabel', { locale: LOCALE })}</th>
                            <th className="rh-a2-r">{t('webAdmin.fakturaceNova.itemTotalLabel', { locale: LOCALE })}</th>
                            <th aria-hidden="true" />
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((item, index) => (
                            <tr key={index} data-testid={`new-invoice-item-${index}`}>
                              <td data-label={t('webAdmin.fakturaceNova.itemCatalogLabel', { locale: LOCALE })}>
                                <select
                                  className="rh-input"
                                  value={item.catalogItemId ?? ''}
                                  onChange={(e) => {
                                    const found = catalog.find((c) => c.id === e.target.value);
                                    updateItem(index, found !== undefined ? emptyItem(found) : { catalogItemId: null });
                                  }}
                                >
                                  <option value="">{t('webAdmin.fakturaceNova.itemCatalogNone', { locale: LOCALE })}</option>
                                  {catalog.map((c) => (
                                    <option key={c.id} value={c.id}>
                                      {c.name}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td data-label={t('webAdmin.fakturaceNova.itemDescriptionLabel', { locale: LOCALE })}>
                                <input
                                  required
                                  className="rh-input"
                                  value={item.description}
                                  onChange={(e) => updateItem(index, { description: e.target.value })}
                                  data-testid={`new-invoice-item-description-${index}`}
                                  aria-label={t('webAdmin.fakturaceNova.itemDescriptionLabel', { locale: LOCALE })}
                                />
                              </td>
                              {/*
                                Krok UXD2: mnozstvi, cena a DPH jdou pres
                                `CzechNumberInput`. Doted to byl surovy text, ktery
                                se posilal do API tak, jak ho clovek napsal —
                                „1 250 000,50" tedy skoncilo chybou 422 a nahled
                                radku ukazal „0,00 Kč" (`toAmount` z toho udelal NaN).
                              */}
                              <td data-label={t('webAdmin.fakturaceNova.itemQuantityLabel', { locale: LOCALE })}>
                                <CzechNumberInput
                                  required
                                  className="rh-input"
                                  value={item.quantity}
                                  onValueChange={(next) => updateItem(index, { quantity: next })}
                                  testId={`new-invoice-item-quantity-${index}`}
                                  ariaLabel={t('webAdmin.fakturaceNova.itemQuantityLabel', { locale: LOCALE })}
                                />
                              </td>
                              <td data-label={t('webAdmin.fakturaceNova.itemUnitPriceLabel', { locale: LOCALE })}>
                                <CzechNumberInput
                                  required
                                  className="rh-input"
                                  value={item.unitPriceWithoutVat}
                                  onValueChange={(next) => updateItem(index, { unitPriceWithoutVat: next })}
                                  testId={`new-invoice-item-price-${index}`}
                                  ariaLabel={t('webAdmin.fakturaceNova.itemUnitPriceLabel', { locale: LOCALE })}
                                />
                              </td>
                              <td data-label={t('webAdmin.fakturaceNova.itemVatLabel', { locale: LOCALE })}>
                                <CzechNumberInput
                                  required
                                  className="rh-input"
                                  value={item.vatRatePercent}
                                  onValueChange={(next) => updateItem(index, { vatRatePercent: next })}
                                  testId={`new-invoice-item-vat-${index}`}
                                  ariaLabel={t('webAdmin.fakturaceNova.itemVatLabel', { locale: LOCALE })}
                                />
                              </td>
                              <td className="rh-a2-r" data-label={t('webAdmin.fakturaceNova.itemTotalLabel', { locale: LOCALE })} data-testid={`new-invoice-item-total-${index}`}>
                                {money(String(lineBase(item)))} Kč
                              </td>
                              <td>
                                {items.length > 1 && (
                                  <button type="button" className="rh-btn" onClick={() => setItems((prev) => prev.filter((_, i) => i !== index))}>
                                    {t('webAdmin.fakturaceNova.itemRemoveButton', { locale: LOCALE })}
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <button type="button" className="rh-btn" onClick={() => setItems((prev) => [...prev, emptyItem(undefined)])} data-testid="new-invoice-item-add">
                      {t('webAdmin.fakturaceNova.itemAddButton', { locale: LOCALE })}
                    </button>
                  </FormSection>
          ) : undefined
        }
        actions={
          <>
            {onCancel !== undefined && (
              <button type="button" className="rh-a2-btn" onClick={onCancel} data-testid="new-invoice-cancel">
                {t('webAdmin.fakturaceNova.cancelButton', { locale: LOCALE })}
              </button>
            )}
            <span className="rh-a2-formbar__spacer" />
            <span className="rh-a2-formbar__sum" data-testid="new-invoice-total">{money(String(summary.total))} Kč</span>
            <SaveStatus status={status} texts={{ saving: '…', saved: '', error: t('webAdmin.fakturaceNova.submitError', { locale: LOCALE }) }} {...errorDetailProp(errorDetail)} />
            {/*
              Krok UXP1 (nalez P1-7): tlacitko bylo sede BEZ DUVODU. Ted je
              duvod vzdy vedle nej a rika, co ma makler udelat.
            */}
            {blockedReason === null ? null : (
              <span className="text-sm text-gray-600" role="status" data-testid="new-invoice-submit-blocked">
                {blockedReason}
              </span>
            )}
            <button type="submit" className="rh-a2-btn rh-a2-btn--primary" disabled={blockedReason !== null} data-testid="new-invoice-submit">
              {t('webAdmin.fakturaceNova.submitButton', { locale: LOCALE })}
            </button>
          </>
        }
        aside={
          <FormSection title={t('webAdmin.fakturaceNova.summaryHeading', { locale: LOCALE })}>
            <FieldRow label={t('webAdmin.fakturaceNova.issueDateLabel', { locale: LOCALE })} htmlFor="new-invoice-issue-date" required>
              <DateField id="new-invoice-issue-date" required className="rh-input" value={issueDate} onValueChange={setIssueDate} />
            </FieldRow>
            <FieldRow label={t('webAdmin.fakturaceNova.dueDateLabel', { locale: LOCALE })} htmlFor="new-invoice-due-date" required>
              <DateField id="new-invoice-due-date" required className="rh-input" value={dueDate} onValueChange={setDueDate} />
            </FieldRow>
            <FieldRow label={t('webAdmin.fakturaceNova.languageLabel', { locale: LOCALE })} htmlFor="new-invoice-language">
              <select id="new-invoice-language" className="rh-input" value={language} onChange={(e) => setLanguage(e.target.value as 'cs' | 'en')}>
                <option value="cs">{t('webAdmin.fakturaceNova.languageCs', { locale: LOCALE })}</option>
                <option value="en">{t('webAdmin.fakturaceNova.languageEn', { locale: LOCALE })}</option>
              </select>
            </FieldRow>
            <ul className="rh-a2-facts rh-a2-facts--sum">
              <li>
                <span className="rh-a2-k">{t('webAdmin.fakturaceNova.summaryBaseLabel', { locale: LOCALE })}</span>
                <span className="rh-a2-v" data-testid="new-invoice-summary-base">{money(String(summary.base))} Kč</span>
              </li>
              <li>
                <span className="rh-a2-k">{t('webAdmin.fakturaceNova.summaryVatLabel', { locale: LOCALE })}</span>
                <span className="rh-a2-v" data-testid="new-invoice-summary-vat">{money(String(summary.vat))} Kč</span>
              </li>
              <li className="rh-a2-facts__total">
                <span className="rh-a2-k">{t('webAdmin.fakturaceNova.summaryTotalLabel', { locale: LOCALE })}</span>
                <span className="rh-a2-v" data-testid="new-invoice-summary-total">{money(String(summary.total))} Kč</span>
              </li>
            </ul>
          </FormSection>
        }
        main={
          <>
            <FormSection title={t('webAdmin.fakturaceNova.modeLabel', { locale: LOCALE })}>
              {/*
                Krok UXF3 bod 3 (#8, pravidlo 3): PRVNI volba je jen „Ze zakazky /
                Samostatna faktura" na vlastnim radku — teprve podle ni se vybira
                odberatel. Driv stal vyber klienta VEDLE typu dokladu, takze dve
                alternativy soutezily o pozornost a nebylo poznat, co se prebira.
                Prepinac je `.rh-a2-toggle` jako u Noveho klienta, ne novy vzor.
              */}
              <div className="rh-a2-toggle" role="radiogroup" aria-label={t('webAdmin.fakturaceNova.modeLabel', { locale: LOCALE })} data-testid="new-invoice-mode">
                <button
                  type="button"
                  className={`rh-a2-toggle__btn${mode === 'commission' ? ' is-on' : ''}`}
                  aria-pressed={mode === 'commission'}
                  onClick={() => setMode('commission')}
                  data-testid="new-invoice-mode-commission"
                >
                  {t('webAdmin.fakturaceNova.modeCommission', { locale: LOCALE })}
                </button>
                <button
                  type="button"
                  className={`rh-a2-toggle__btn${mode === 'free' ? ' is-on' : ''}`}
                  aria-pressed={mode === 'free'}
                  onClick={() => setMode('free')}
                  data-testid="new-invoice-mode-free"
                >
                  {t('webAdmin.fakturaceNova.modeFree', { locale: LOCALE })}
                </button>
              </div>

              <FieldGrid columns="1fr">
                <FieldRow label={t('webAdmin.fakturaceNova.partySearchLabel', { locale: LOCALE })} htmlFor="new-invoice-party">
                  <input
                    id="new-invoice-party"
                    className="rh-input block"
                    placeholder={t('webAdmin.fakturaceNova.partySearchPlaceholder', { locale: LOCALE })}
                    value={partyQuery}
                    onChange={(e) => {
                      setPartyQuery(e.target.value);
                      setPartyId(null);
                    }}
                    data-testid="new-invoice-party-search"
                  />
                  {partyResults.length > 0 && (
                    <ul className="rh-card mt-1 max-h-40 overflow-auto" data-testid="new-invoice-party-results">
                      {partyResults.map((party) => (
                        <li key={party.id}>
                          <button type="button" className="w-full p-1 text-left hover:bg-gray-50" onClick={() => void selectParty(party)} data-testid={`new-invoice-party-option-${party.id}`}>
                            {party.displayName}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  {partyQuery.trim() !== '' && partyResults.length === 0 && partyId === null && (
                    <p className="text-sm text-gray-500">{t('webAdmin.fakturaceNova.partyNoResults', { locale: LOCALE })}</p>
                  )}
                  {mode === 'free' && partyId === null && (
                    <label className="mt-2 flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={saveAsClient}
                        onChange={(e) => setSaveAsClient(e.target.checked)}
                        data-testid="new-invoice-save-as-client"
                      />
                      {t('webAdmin.fakturaceNova.saveAsClientLabel', { locale: LOCALE })}
                    </label>
                  )}
                </FieldRow>
              </FieldGrid>

              {!vatPayer && <p className="text-sm text-gray-500">{t('webAdmin.fakturaceNova.vatPayerNote', { locale: LOCALE })}</p>}

              {/* Krok UXF3 bod 3 (#8): zakazku se ptame jen v rezimu „Ze zakazky" —
                  v samostatne fakture by to byla treti soubezna cesta k odberateli. */}
              {mode === 'commission' && (
              <FieldRow label={t('webAdmin.fakturaceNova.caseLabel', { locale: LOCALE })} htmlFor="new-invoice-case">
                {/*
                  Krok UXP1 (nalez P1-7): pole chtelo INTERNI UUID. Kdo napsal
                  „Husova", dostal 400 a prazdnou obrazovku. Hledani zakazky
                  podle adresy/nazvu je TATAZ cesta jako v „Nové smlouvě" —
                  spolecna funkce v `lib/case-address-lookup.ts`, ne druhy
                  seznam; od PROP3B se pta serveru (`searchCasesByName`).
                */}
                {initialCaseId !== undefined ? (
                  /*
                    Faktura otevrena Z DETAILU ZAKAZKY zakazku nemeni — makler
                    by tu jinak cetl jeji UUID (nalez P1-7). Nazev se nacte
                    s kontextem zakazky.
                  */
                  <p className="font-semibold m-0" data-testid="new-invoice-case-fixed">
                    {caseContext?.title ?? t('webAdmin.fakturaceNova.caseLoading', { locale: LOCALE })}
                  </p>
                ) : (
                <div className="flex gap-2">
                  <input
                    id="new-invoice-case"
                    className="rh-input"
                    value={caseQuery}
                    placeholder={t('webAdmin.fakturaceNova.casePlaceholder', { locale: LOCALE })}
                    onChange={(e) => { setCaseQuery(e.target.value); }}
                    data-testid="new-invoice-case-id"
                  />
                  <button type="button" className="rh-btn" onClick={() => void loadCase()} disabled={caseId.trim() === ''} data-testid="new-invoice-case-load">
                    {t('webAdmin.fakturaceNova.caseLoadButton', { locale: LOCALE })}
                  </button>
                </div>
                )}
                {caseQuery.trim() !== '' && caseIndex !== null && (
                  <ul className="mt-2 flex flex-col gap-1" data-testid="new-invoice-case-options">
                    {matchingCases(caseIndex, caseQuery).map(([id, name]) => (
                      <li key={id}>
                        <button
                          type="button"
                          className="rh-btn"
                          onClick={() => { void pickCase(id, name); }}
                          data-testid={`new-invoice-case-option-${id}`}
                        >
                          {name}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {/* Krok PROP3B: kvota ani vypadek se nesmi tvarit jako „zadna takova zakazka". */}
                {caseSearchError !== null && (
                  <p className="mt-2 text-sm" style={{ color: 'var(--rh-text-muted)' }} data-testid="new-invoice-case-search-error">
                    {caseSearchError}
                  </p>
                )}
                {caseContext !== null && (
                  <div className="mt-2 text-sm" data-testid="new-invoice-case-context">
                    <p className="font-semibold">{caseContext.title}</p>
                    {caseContext.participants.length === 0 ? (
                      <p className="text-gray-500">{t('webAdmin.fakturaceNova.caseNoParticipants', { locale: LOCALE })}</p>
                    ) : (
                      <ul className="flex flex-wrap gap-2">
                        {caseContext.participants.map((p) => (
                          <li key={`${p.partyId}-${p.role}`}>
                            {/* Krok UXP1: role lidsky („Prodávající"), ne kod `seller` — tentyz slovnik jako karta „Lidé". */}
                            <button type="button" className="rh-btn" onClick={() => selectParticipant(p)} data-testid={`new-invoice-participant-${p.partyId}`}>
                              {caseRoleCaption(p.role)}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </FieldRow>
              )}
            </FormSection>

            <FormSection title={t('webAdmin.fakturaceNova.customerHeading', { locale: LOCALE })}>
              {/*
                Krok UXF3 bod 3 (#8, pravidlo 3): kdyz se udaje PREBRALY z klienta
                nebo ze zakazky, nestoji tu znovu cely formular — je videt prehled
                a „Upravit udaje". Jinak uzivatel nevi, co se prevzalo a co ma psat.
              */}
              {/*
                Krok FAK1 bod 1: chybejici adresa se rika TED, ne az chybou 422
                po stisku „Vystavit" — makler jinak posle doklad a dozvi se to
                od MyUcta.
              */}
              {partyId !== null && !customerAddressComplete(customer) && (
                <p role="alert" className="rh-a2-error" data-testid="new-invoice-customer-address-missing">
                  {t('webAdmin.fakturaceNova.addressMissingWarning', { locale: LOCALE })}
                </p>
              )}
              {partyId !== null && !customerEditing ? (
                <>
                  <ul className="rh-a2-facts" data-testid="new-invoice-customer-overview">
                    <li>
                      <span className="rh-a2-k">{t('webAdmin.fakturaceNova.nameLabel', { locale: LOCALE })}</span>
                      <span className="rh-a2-v">{customer.name}</span>
                    </li>
                    <li>
                      <span className="rh-a2-k">{t('webAdmin.fakturaceNova.streetLabel', { locale: LOCALE })}</span>
                      <span className="rh-a2-v" data-testid="new-invoice-customer-address">{[customer.street, customer.zip, customer.city].filter((v) => v.trim() !== '').join(', ')}</span>
                      {prefillSourceNote(prefillSources?.address) !== null && (
                        <span className="text-xs text-gray-600" data-testid="new-invoice-customer-address-source">
                          {prefillSourceNote(prefillSources?.address)}
                        </span>
                      )}
                    </li>
                    {customer.ico !== null && customer.ico.trim() !== '' && (
                      <li>
                        <span className="rh-a2-k">{t('webAdmin.fakturaceNova.icoLabel', { locale: LOCALE })}</span>
                        <span className="rh-a2-v" data-testid="new-invoice-customer-ico-value">{customer.ico}</span>
                        {prefillSourceNote(prefillSources?.ico) !== null && (
                          <span className="text-xs text-gray-600" data-testid="new-invoice-customer-ico-source">
                            {prefillSourceNote(prefillSources?.ico)}
                          </span>
                        )}
                      </li>
                    )}
                    {customer.dic !== null && customer.dic.trim() !== '' && (
                      <li>
                        <span className="rh-a2-k">{t('webAdmin.fakturaceNova.dicLabel', { locale: LOCALE })}</span>
                        <span className="rh-a2-v" data-testid="new-invoice-customer-dic-value">{customer.dic}</span>
                      </li>
                    )}
                    {customer.email !== null && customer.email.trim() !== '' && (
                      <li>
                        <span className="rh-a2-k">{t('webAdmin.fakturaceNova.emailLabel', { locale: LOCALE })}</span>
                        <span className="rh-a2-v">{customer.email}</span>
                      </li>
                    )}
                  </ul>
                  <button type="button" className="rh-a2-btn" onClick={() => setCustomerEditing(true)} data-testid="new-invoice-customer-edit">
                    {t('webAdmin.fakturaceNova.customerEditButton', { locale: LOCALE })}
                  </button>
                </>
              ) : (
                <>
              <FieldRow label={t('webAdmin.fakturaceNova.nameLabel', { locale: LOCALE })} htmlFor="new-invoice-customer-name" required>
                <input id="new-invoice-customer-name" required className="rh-input block" value={customer.name} onChange={(e) => setCustomer((prev) => ({ ...prev, name: e.target.value }))} data-testid="new-invoice-customer-name" />
              </FieldRow>
              <FieldGrid columns="1fr 1fr">
                <IcoAresField
                  id="new-invoice-customer-ico"
                  label={t('webAdmin.fakturaceNova.icoLabel', { locale: LOCALE })}
                  value={customer.ico ?? ''}
                  onChange={(ico) => setCustomer((prev) => ({ ...prev, ico }))}
                  onFound={(company) =>
                    setCustomer((prev) => ({
                      ...prev,
                      name: company.companyName,
                      street: company.street,
                      city: company.city,
                      zip: company.zip,
                      dic: company.dic ?? prev.dic,
                    }))
                  }
                />
                <FieldRow label={t('webAdmin.fakturaceNova.dicLabel', { locale: LOCALE })} htmlFor="new-invoice-customer-dic">
                  <input id="new-invoice-customer-dic" className="rh-input block" value={customer.dic ?? ''} onChange={(e) => setCustomer((prev) => ({ ...prev, dic: e.target.value === '' ? null : e.target.value }))} />
                </FieldRow>
              </FieldGrid>
              <FieldGrid columns="2fr 1fr 1fr">
                <FieldRow label={t('webAdmin.fakturaceNova.streetLabel', { locale: LOCALE })} htmlFor="new-invoice-customer-street" required>
                  <input id="new-invoice-customer-street" required aria-invalid={customer.street.trim() === ''} className="rh-input block" value={customer.street} onChange={(e) => setCustomer((prev) => ({ ...prev, street: e.target.value }))} data-testid="new-invoice-customer-street" />
                </FieldRow>
                <FieldRow label={t('webAdmin.fakturaceNova.cityLabel', { locale: LOCALE })} htmlFor="new-invoice-customer-city" required>
                  <input id="new-invoice-customer-city" required aria-invalid={customer.city.trim() === ''} className="rh-input block" value={customer.city} onChange={(e) => setCustomer((prev) => ({ ...prev, city: e.target.value }))} data-testid="new-invoice-customer-city" />
                </FieldRow>
                <FieldRow label={t('webAdmin.fakturaceNova.zipLabel', { locale: LOCALE })} htmlFor="new-invoice-customer-zip" required>
                  <input id="new-invoice-customer-zip" required aria-invalid={customer.zip.trim() === ''} className="rh-input block" value={customer.zip} onChange={(e) => setCustomer((prev) => ({ ...prev, zip: e.target.value }))} data-testid="new-invoice-customer-zip" />
                </FieldRow>
              </FieldGrid>
              <FieldRow label={t('webAdmin.fakturaceNova.emailLabel', { locale: LOCALE })} htmlFor="new-invoice-customer-email">
                <input id="new-invoice-customer-email" type="email" className="rh-input block" value={customer.email ?? ''} onChange={(e) => setCustomer((prev) => ({ ...prev, email: e.target.value === '' ? null : e.target.value }))} />
              </FieldRow>
                </>
              )}
            </FormSection>

            {/*
              Krok UXD2: tabulka polozek je v plnosirkovem pruhu `FormLayout`
              (slot `wide`), ne v uzkem levem sloupci — v nem se na 1440 px
              rolovala vodorovne. Provize do uzkeho sloupce patri, zustava tu.
            */}
            {mode === 'free' ? null : (
              <FormSection title={t('webAdmin.fakturaceNova.commissionTypeLabel', { locale: LOCALE })}>
                <FieldRow label={t('webAdmin.fakturaceNova.commissionBasisLabel', { locale: LOCALE })} htmlFor="new-invoice-basis" required>
                  {/* Krok UXD2: zaklad provize je CASTKA — cesky zapis, kanonicka hodnota do API. */}
                  <CzechNumberInput id="new-invoice-basis" required className="rh-input block" value={basisAmount} onValueChange={setBasisAmount} testId="new-invoice-basis" />
                  {/*
                    Krok P8F8 bod 2: zaklad je pomocna kalkulacka „% z ceny" —
                    cenu zada MAKLER. Nabidkova cena nemovitosti se sem uz
                    nepredvyplnuje (R.B. 27. 10.: provize se meni).
                  */}
                  <span className="text-xs text-gray-600">
                    {t('webAdmin.fakturaceNova.commissionBasisHint', { locale: LOCALE })}
                  </span>
                </FieldRow>
                <FieldRow label={t('webAdmin.fakturaceNova.commissionTypeLabel', { locale: LOCALE })}>
                  <select className="rh-input block" value={commissionType} onChange={(e) => setCommissionType(e.target.value as 'percent' | 'fixed')} data-testid="new-invoice-commission-type">
                    <option value="percent">{t('webAdmin.fakturaceNova.commissionTypePercent', { locale: LOCALE })}</option>
                    <option value="fixed">{t('webAdmin.fakturaceNova.commissionTypeFixed', { locale: LOCALE })}</option>
                  </select>
                </FieldRow>
                <FieldRow label={t('webAdmin.fakturaceNova.commissionValueLabel', { locale: LOCALE })} htmlFor="new-invoice-commission-value" required>
                  <CzechNumberInput id="new-invoice-commission-value" required className="rh-input block" value={commissionValue} onValueChange={setCommissionValue} testId="new-invoice-commission-value" />
                  {/*
                    Krok P8F8 bod 2 — NABIDKA VEDLE POLE, NE PREDVYPLNENI.
                    Pole „Provize" zacina prazdne; kdyz zakazka ma PODEPSANOU
                    smlouvu se sjednanou provizi, stoji tu castka ke prevzeti
                    jednim klepnutim. Dve podepsane smlouvy = dva radky
                    (vyber), koncept smlouvy = zadny radek.
                  */}
                  {commissionOffers.length > 0 && (
                    <div className="mt-1 flex flex-col gap-1" data-testid="new-invoice-commission-offers">
                      <span className="text-xs text-gray-600">
                        {t('webAdmin.fakturaceNova.commissionOfferHeading', { locale: LOCALE })}
                      </span>
                      {commissionOffers.map((offer, index) => (
                        <span key={offer.contractId} className="text-xs" data-testid={`new-invoice-commission-offer-${index}`}>
                          <button
                            type="button"
                            className="rh-link"
                            onClick={() => takeCommissionOffer(offer)}
                            data-testid={`new-invoice-commission-offer-use-${index}`}
                          >
                            {t(
                              offer.source === 'contract-commission'
                                ? 'webAdmin.fakturaceNova.commissionOfferUse'
                                : 'webAdmin.fakturaceNova.commissionOfferUsePrice',
                              { locale: LOCALE, values: { amount: moneyShort(offer.amount, 'CZK') } },
                            )}
                          </button>{' '}
                          <span className="text-gray-600">
                            {offer.signedOn === null
                              ? t('webAdmin.fakturaceNova.commissionOfferContractUndated', {
                                  locale: LOCALE,
                                  values: { title: offer.contractTitle },
                                })
                              : t('webAdmin.fakturaceNova.commissionOfferContract', {
                                  locale: LOCALE,
                                  values: { title: offer.contractTitle, date: czDate(offer.signedOn) },
                                })}
                          </span>
                        </span>
                      ))}
                    </div>
                  )}
                  {takenOffer !== null && (
                    <span className="text-xs text-gray-600" data-testid="new-invoice-commission-offer-source">
                      {t(OFFER_SOURCE_KEY[takenOffer.source], { locale: LOCALE })}
                    </span>
                  )}
                </FieldRow>
                <FieldRow label={t('webAdmin.fakturaceNova.commissionDescriptionLabel', { locale: LOCALE })} htmlFor="new-invoice-commission-description" required>
                  <input id="new-invoice-commission-description" required className="rh-input block" value={commissionDescription} onChange={(e) => setCommissionDescription(e.target.value)} data-testid="new-invoice-commission-description" />
                </FieldRow>
                <FieldRow label={t('webAdmin.fakturaceNova.commissionVatLabel', { locale: LOCALE })} htmlFor="new-invoice-commission-vat">
                  <CzechNumberInput id="new-invoice-commission-vat" required className="rh-input w-20" value={commissionVat} onValueChange={setCommissionVat} testId="new-invoice-commission-vat" />
                </FieldRow>
                {/*
                  Krok KLIKFIX1 (vada V5): co je na provizi podezrele. Nic se
                  neprepocitava samo (R46 — provizi zadava makler), formular
                  jen rekne, co vidi.
                */}
                {commissionNotes.map((note) => (
                  <p
                    key={note}
                    role="alert"
                    className="rh-a2-error text-sm"
                    data-testid={`new-invoice-commission-warn-${note}`}
                  >
                    {t(COMMISSION_WARNING_KEY[note], { locale: LOCALE })}
                  </p>
                ))}
              </FormSection>
            )}
          </>
        }
      />
    </form>
  );
}

// --------------------------------------------------------------------- katalog

interface CatalogForm {
  name: string;
  description: string;
  unit: string;
  defaultUnitPrice: string;
  vatRatePercent: string;
}

const EMPTY_CATALOG_FORM: CatalogForm = { name: '', description: '', unit: 'ks', defaultUnitPrice: '', vatRatePercent: '21' };

export function CatalogSection(): React.JSX.Element {
  const [items, setItems] = React.useState<CatalogItemDto[]>([]);
  const [form, setForm] = React.useState<CatalogForm>(EMPTY_CATALOG_FORM);
  const [status, setStatus] = React.useState<SaveStatusKind>('idle');
  const [errorDetail, setErrorDetail] = React.useState<string | undefined>(undefined);

  const reload = React.useCallback(async () => {
    setItems(await listCatalogItems(true));
  }, []);

  React.useEffect(() => {
    void reload();
  }, [reload]);

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setStatus('saving');
    setErrorDetail(undefined);
    try {
      await createCatalogItem({
        name: form.name,
        description: form.description.trim() === '' ? null : form.description,
        unit: form.unit,
        defaultUnitPrice: form.defaultUnitPrice,
        vatRatePercent: form.vatRatePercent,
      });
      setForm(EMPTY_CATALOG_FORM);
      setStatus('saved');
      await reload();
    } catch (error) {
      setStatus('error');
      setErrorDetail(domainErrorMessage(error));
    }
  }

  async function toggleActive(item: CatalogItemDto): Promise<void> {
    await updateCatalogItem(item.id, {
      name: item.name,
      description: item.description,
      unit: item.unit,
      defaultUnitPrice: item.defaultUnitPrice,
      vatRatePercent: item.vatRatePercent,
      active: !item.active,
    });
    await reload();
  }

  return (
    <FormSection title={t('webAdmin.fakturaceKatalog.heading', { locale: LOCALE })}>
      <p className="mb-3 text-sm text-gray-600">{t('webAdmin.fakturaceKatalog.detail', { locale: LOCALE })}</p>

      {items.length === 0 ? (
        <p data-testid="catalog-empty">{t('webAdmin.fakturaceKatalog.empty', { locale: LOCALE })}</p>
      ) : (
        <table className="mb-3 w-full text-sm" data-testid="catalog-table">
          <tbody>
            {items.map((item) => (
              <tr key={item.id} data-testid={`catalog-row-${item.id}`}>
                <td>{item.name}</td>
                <td>{money(item.defaultUnitPrice)} Kč / {item.unit}</td>
                <td>{formatPercent(item.vatRatePercent)} %</td>
                <td>
                  <button type="button" className="rh-btn" onClick={() => void toggleActive(item)} data-testid={`catalog-toggle-${item.id}`}>
                    {item.active ? t('webAdmin.fakturaceKatalog.activeLabel', { locale: LOCALE }) : '—'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <form onSubmit={(e) => void submit(e)} className="flex flex-wrap items-end gap-2">
        <FieldRow label={t('webAdmin.fakturaceKatalog.nameLabel', { locale: LOCALE })}>
          <input required className="rh-input" value={form.name} onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))} data-testid="catalog-form-name" />
        </FieldRow>
        <FieldRow label={t('webAdmin.fakturaceKatalog.unitLabel', { locale: LOCALE })}>
          <input className="rh-input w-20" value={form.unit} onChange={(e) => setForm((prev) => ({ ...prev, unit: e.target.value }))} />
        </FieldRow>
        <FieldRow label={t('webAdmin.fakturaceKatalog.priceLabel', { locale: LOCALE })}>
          <input required className="rh-input w-28" value={form.defaultUnitPrice} onChange={(e) => setForm((prev) => ({ ...prev, defaultUnitPrice: e.target.value }))} data-testid="catalog-form-price" />
        </FieldRow>
        <FieldRow label={t('webAdmin.fakturaceKatalog.vatLabel', { locale: LOCALE })}>
          <input required className="rh-input w-16" value={form.vatRatePercent} onChange={(e) => setForm((prev) => ({ ...prev, vatRatePercent: e.target.value }))} />
        </FieldRow>
        <button type="submit" className="rh-btn" data-testid="catalog-form-submit">
          {t('webAdmin.fakturaceKatalog.addButton', { locale: LOCALE })}
        </button>
        <SaveStatus status={status} texts={{ saving: '…', saved: '', error: '' }} {...errorDetailProp(errorDetail)} />
      </form>
    </FormSection>
  );
}

// ------------------------------------------------------------ zakazka Obchod

export function CaseInvoicesPanel({ caseId }: { readonly caseId: string }): React.JSX.Element {
  const [showForm, setShowForm] = React.useState(false);
  return (
    <div className="flex flex-col gap-4" data-testid="case-obchod-panel">
      <div className="flex items-center justify-between">
        <h3 className="font-bold">{t('webAdmin.caseObchod.heading', { locale: LOCALE })}</h3>
        <button type="button" className="rh-btn rh-btn--primary" onClick={() => setShowForm((v) => !v)} data-testid="case-invoice-commission-button">
          {t('webAdmin.caseObchod.invoiceButton', { locale: LOCALE })}
        </button>
      </div>
      {showForm && <NewInvoiceTabForCase caseId={caseId} onCancel={() => setShowForm(false)} />}
      <InvoicesListTab caseId={caseId} />
    </div>
  );
}

/** Nacte dodavatele aktualniho uzivatele (overview) a zabali NewInvoiceTab pro pouziti mimo `/fakturace`. */
function NewInvoiceTabForCase({
  caseId,
  onCancel,
}: {
  readonly caseId: string;
  readonly onCancel: () => void;
}): React.JSX.Element {
  const [supplierId, setSupplierId] = React.useState<string | null | undefined>(undefined);
  const [vatPayer, setVatPayer] = React.useState(true);

  React.useEffect(() => {
    void getBillingOverview().then(
      (overview) => {
        setSupplierId(overview.supplier?.id ?? null);
        setVatPayer(overview.supplier?.vatPayer ?? true);
      },
      () => setSupplierId(null),
    );
  }, []);

  if (supplierId === undefined) return <p role="status">…</p>;
  return <NewInvoiceTab supplierId={supplierId} vatPayer={vatPayer} initialCaseId={caseId} onCancel={onCancel} />;
}
